import * as THREE from 'three';
import {
    scene,
    camera,
    renderer,
    controls,
    floor,
    grid,
    fadePlane,
    modelRoot,
    animationPaused,
    setAnimationPaused
} from './state.js';

/**
 * Handover capture for the eila AI Vision Studio (#233679): high-res views of the current
 * configuration plus color coded masks, so the image model knows what it must not change.
 *
 * Mask colors (legend is repeated in the studio prompt, AiVisionStudioConfig::MASK_LEGEND):
 *  blue  = windows and glass
 *  red   = doors and flaps
 *  green = printable exterior surfaces (configurator color groups)
 *  gray  = everything else (fixed structure)
 *
 * Classification uses the naming conventions of the GLB files: materials "configurator-glass*"
 * or containing "glass"/"glas" are windows, nodes/materials containing "door"/"tuer"/"klappe" are
 * doors, materials "configurator-base*" and "Unitfarbe*" are printable unless they carry "noDecals"
 * (e.g. the driver cab). Only meshes of the loaded model are part of the mask. Models can mark parts
 * explicitly with the prefixes "aivision-glass", "aivision-door" and "aivision-print".
 */

const MASK_COLORS = {
    glass: 0x1e5bff,
    door: 0xff2020,
    print: 0x21c55d,
    fixed: 0x8c8c8c,
};

const VIEW_KEYS = ['three_quarter', 'front', 'left', 'right', 'back'];

function classifyMesh(mesh) {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const materialNames = materials.map((material) => (material?.name || '').toLowerCase()).join(' ');
    const nodeNames = [];
    mesh.traverseAncestors((ancestor) => nodeNames.push((ancestor.name || '').toLowerCase()));
    const names = `${(mesh.name || '').toLowerCase()} ${nodeNames.join(' ')} ${materialNames}`;

    if (/aivision-glass/.test(names)) return 'glass';
    if (/aivision-door/.test(names)) return 'door';
    if (/aivision-print/.test(names)) return 'print';
    if (/door|tuer|tür|klappe/.test(names)) return 'door';
    if (/glass|glas/.test(materialNames)) return 'glass';
    if (/configurator-base|unitfarbe/.test(materialNames) && !/nodecals/.test(materialNames)) return 'print';
    return 'fixed';
}

function belongsToModel(object) {
    if (!modelRoot) {
        return true;
    }
    let current = object;
    while (current) {
        if (current === modelRoot) {
            return true;
        }
        current = current.parent;
    }
    return false;
}

function isHelper(object) {
    return object === grid || object === floor || object === fadePlane
        || (object.name && (object.name.startsWith('decal-frame-') || object.name.startsWith('marker')));
}

function computeFraming() {
    const bbox = new THREE.Box3();
    scene.traverse((object) => {
        if (!object.isMesh || !object.visible || isHelper(object)) {
            return;
        }
        const objectBox = new THREE.Box3().setFromObject(object);
        const size = objectBox.getSize(new THREE.Vector3());
        if (size.x > 1000 || size.y > 1000 || size.z > 1000) {
            return;
        }
        bbox.expandByObject(object);
    });
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = THREE.MathUtils.degToRad(camera.fov);
    const distance = (maxDim / (2 * Math.tan(fov / 2))) * 0.85;
    return {center, distance};
}

function viewPosition(key, center, distance) {
    const angle = Math.PI / 4;
    switch (key) {
        case 'front':
            return new THREE.Vector3(center.x, center.y, center.z + distance);
        case 'back':
            return new THREE.Vector3(center.x, center.y, center.z - distance);
        case 'left':
            return new THREE.Vector3(center.x - distance, center.y, center.z);
        case 'right':
            return new THREE.Vector3(center.x + distance, center.y, center.z);
        case 'top':
            // a minimal z offset keeps lookAt stable when looking straight down
            return new THREE.Vector3(center.x, center.y + distance, center.z + 0.001);
        default:
            return new THREE.Vector3(
                center.x + distance * Math.cos(angle),
                center.y + distance * 0.1,
                center.z + distance * Math.sin(angle)
            );
    }
}

function toDataUrl(canvas, lossless) {
    // webp keeps the payload small; browsers without webp encoding return png
    const dataUrl = canvas.toDataURL(lossless ? 'image/png' : 'image/webp', 0.92);
    return dataUrl.startsWith('data:image/webp') || dataUrl.startsWith('data:image/png') ? dataUrl : canvas.toDataURL('image/png');
}

/**
 * @param {Object} [options]
 * @param {string[]} [options.viewKeys] views to render, default the handover views (export adds "top")
 * @returns {Promise<{views: Object<string,string>, masks: Object<string,string>}>}
 */
export async function captureAiVisionHandover({scale = 2, withMasks = true, viewKeys = VIEW_KEYS} = {}) {
    if (!renderer || !camera || !controls || !scene) {
        return {views: {}, masks: {}};
    }

    const originalPosition = camera.position.clone();
    const originalTarget = controls.target.clone();
    const originalEnabled = controls.enabled;
    const originalAspect = camera.aspect;
    const originalSize = new THREE.Vector2();
    renderer.getSize(originalSize);
    const originalBackground = scene.background;
    const originalEnvironment = scene.environment;
    const wasAnimationPaused = animationPaused;

    const width = 1200 * scale;
    const height = 800 * scale;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    setAnimationPaused(true);
    controls.enabled = false;

    const hidden = [];
    scene.traverse((object) => {
        if (object.visible && object.name && object.name.startsWith('decal-frame-')) {
            hidden.push(object);
            object.visible = false;
        }
    });

    const {center, distance} = computeFraming();
    const views = {};
    const masks = {};

    const render = (key) => {
        camera.position.copy(viewPosition(key, center, distance));
        camera.lookAt(center);
        camera.updateMatrixWorld(true);
        camera.updateProjectionMatrix();
        renderer.render(scene, camera);
    };

    try {
        for (const key of viewKeys) {
            render(key);
            views[key] = toDataUrl(renderer.domElement, false);
        }

        if (withMasks) {
            // flat colors per part class, helpers and background hidden
            const swapped = [];
            const maskMaterials = {};
            for (const [type, color] of Object.entries(MASK_COLORS)) {
                maskMaterials[type] = new THREE.MeshBasicMaterial({color, side: THREE.DoubleSide});
            }
            scene.traverse((object) => {
                if (object.visible && (isHelper(object) || (object.isMesh && !belongsToModel(object)))) {
                    hidden.push(object);
                    object.visible = false;
                    return;
                }
                if (object.isMesh && object.visible) {
                    swapped.push([object, object.material]);
                    object.material = maskMaterials[classifyMesh(object)];
                }
            });
            scene.background = new THREE.Color(0xffffff);
            scene.environment = null;

            try {
                const maskScale = Math.max(1, Math.round(scale / 2));
                renderer.setSize(1200 * maskScale, 800 * maskScale, false);
                for (const key of viewKeys) {
                    render(key);
                    masks[key] = toDataUrl(renderer.domElement, true);
                }
            } finally {
                for (const [object, material] of swapped) {
                    object.material = material;
                }
                Object.values(maskMaterials).forEach((material) => material.dispose());
                scene.background = originalBackground;
                scene.environment = originalEnvironment;
            }
        }
    } finally {
        hidden.forEach((object) => {
            object.visible = true;
        });
        renderer.setSize(originalSize.x, originalSize.y, false);
        camera.aspect = originalAspect;
        camera.updateProjectionMatrix();
        camera.position.copy(originalPosition);
        camera.lookAt(originalTarget);
        controls.target.copy(originalTarget);
        controls.enabled = originalEnabled;
        camera.updateMatrixWorld(true);
        controls.update();
        for (let i = 0; i < 3; i++) {
            renderer.render(scene, camera);
            await new Promise((resolve) => setTimeout(resolve, 16));
        }
        setAnimationPaused(wasAnimationPaused);
    }

    return {views, masks};
}
